import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';

import {
    createDataTableColumnHelper,
    type DataTableColumnDef,
} from '@/components/organisms/DataTable';
import { translateDataLabel } from '@/i18n/dataLabels';
import ActiveFishGroupCell from '@/routes/Internal/components/ActiveFishGroupCell';
import { createCagePageUrl } from '@/utils/urlGenerator';

import type { BioscopeRow } from '../bioscopeRows';

const columnHelper = createDataTableColumnHelper<BioscopeRow>();

interface FishGroupColumnOptions {
    /** The `fish-group-tracking` flag. Off, the table and its links are as before fish groups. */
    fishGroupsEnabled: boolean;
    tFishGroups: TFunction<'fishGroups'>;
}

/**
 * Where each bioscope is: its client, region, location and pen, linked to their pages, and with
 * fish groups on, the pen's active fish group.
 */
export function placeColumns(
    t: TFunction,
    { fishGroupsEnabled, tFishGroups }: FishGroupColumnOptions
): DataTableColumnDef<BioscopeRow>[] {
    return columnHelper.columns([
        columnHelper.accessor((row) => row.client?.name, {
            id: 'client',
            enableGlobalFilter: true,
            header: t('internal.bioscopeList.columns.client'),
            cell: (info) => (
                <Link to={`/internal/clients/${info.row.original.clientId}`}>
                    {info.getValue()}
                </Link>
            ),
        }),
        columnHelper.accessor((row) => row.client?.name, {
            id: 'region',
            enableGlobalFilter: true,
            header: t('internal.bioscopeList.columns.region'),
            cell: (info) => <Link to={`/c/${info.row.original.clientId}`}>{info.getValue()}</Link>,
        }),
        columnHelper.accessor((row) => row.lastLocation?.cage?.location?.name, {
            id: 'location',
            header: t('internal.bioscopeList.columns.location'),
            cell: (info) => {
                return (
                    <Link
                        to={`/c/${info.row.original.clientId}/location/${info.row.original.lastLocation?.cage?.location?.id}`}>
                        {' '}
                        {info.getValue()}
                    </Link>
                );
            },
            // A bioscope on land has no location name, and the table sorts it after every
            // location in both directions, so this only compares names.
            sortFn: (a, b) =>
                (a.original.lastLocation?.cage?.location?.name ?? '')
                    .toLocaleLowerCase()
                    .localeCompare(
                        (b.original.lastLocation?.cage?.location?.name ?? '').toLocaleLowerCase()
                    ),
        }),
        columnHelper.accessor(
            // The PEN's species, not its location's: a location can hold several (WB-2207).
            // Named optional rather than cast, so an unrecorded species reads as a dash
            // instead of the key leaking through t(). The dash is drawn, not the value, so the
            // pen sorts with the other empty cells.
            (row) => {
                const fishType = row.lastLocation?.cage?.fishTypes?.name;

                return fishType ? translateDataLabel(t, 'fishTypes', fishType) : undefined;
            },
            {
                id: 'fishType',
                header: t('internal.bioscopeList.columns.fishType'),
                cell: (row) => {
                    return row.getValue() ?? '-';
                },
            }
        ),
        columnHelper.accessor(
            (row) => {
                const cageType = row.lastLocation?.cage?.cageType;
                return cageType ? translateDataLabel(t, 'cageTypes', cageType) : undefined;
            },
            {
                id: 'cageType',
                header: t('internal.bioscopeList.columns.cageType'),
                cell: (row) => {
                    return row.getValue();
                },
            }
        ),
        columnHelper.accessor((row) => row.lastLocation?.cage?.name, {
            id: 'pen',
            header: t('internal.bioscopeList.columns.pen'),
            cell: (info) => {
                const { clientId, lastLocation } = info.row.original;
                const cage = lastLocation?.cage;
                const locationId = cage?.location?.id;
                if (!clientId || !cage || !locationId) {
                    return info.getValue() ?? null;
                }

                // Pinned to the pen's current cohort: without `fg` the pen opens on its
                // pen-wide legacy view. An empty pen has no cohort to pin.
                const fishGroupId = fishGroupsEnabled ? cage.activeFishGroup?.id : undefined;

                return (
                    <Link
                        to={createCagePageUrl(
                            clientId,
                            locationId,
                            cage.id,
                            '/dashboard',
                            fishGroupId
                        )}>
                        {info.getValue()}
                    </Link>
                );
            },
        }),
        ...(fishGroupsEnabled
            ? [
                  columnHelper.accessor((row) => row.lastLocation?.cage?.activeFishGroup?.name, {
                      id: 'fishGroup',
                      header: tFishGroups('locationOverview.columnHeader'),
                      cell: (info) => (
                          <ActiveFishGroupCell
                              fishGroup={info.row.original.lastLocation?.cage?.activeFishGroup}
                          />
                      ),
                  }),
              ]
            : []),
        columnHelper.accessor((row) => row.lastLocation?.cageId, {
            id: 'cageId',
            header: t('internal.bioscopeList.columns.cageId'),
        }),
    ]);
}
